import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios'
import { SubscriptionLimitError, type SubscriptionLimitType } from '../errors'

/**
 * HTTP Service - Axios instance with interceptors for authentication
 * Handles automatic token injection and refresh logic
 */

// Base URL for API - Update this to match your backend URL
// Default to local backend for development (use HTTP, not HTTPS for localhost)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

// Create axios instance
const http: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 seconds - increased from 10s to handle longer operations like LinkedIn publishing
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor - Add auth token to requests
http.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get access token from localStorage
    const accessToken = localStorage.getItem('access_token')
    
    // Add token to Authorization header if it exists
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    
    // If the data is FormData, remove Content-Type header so axios can set it with boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type']
    }
    
    // Preserve timeout if explicitly set in request config (don't override)
    // The timeout from the request config should take precedence over instance default
    if (config.timeout !== undefined) {
      // Timeout is already set in config, keep it
      console.log('[HTTP Interceptor] Request timeout:', config.timeout, 'ms')
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - Handle token refresh on 401 errors
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: any) => void
}> = []

const processQueue = (error: any = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  })
  failedQueue = []
}

http.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            // Retry original request with new token
            const accessToken = localStorage.getItem('access_token')
            if (accessToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`
            }
            return http(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = localStorage.getItem('refresh_token')

      if (!refreshToken) {
        // No refresh token - just reject, let the app handle the redirect
        // Don't clear tokens here - the auth store will handle that
        processQueue(error)
        isRefreshing = false
        console.warn('[HTTP] No refresh token available, request will fail')
        return Promise.reject(error)
      }

      try {
        // Attempt to refresh token
        const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
          refresh_token: refreshToken
        }, {
          // Don't retry the refresh request itself
          validateStatus: (status) => status < 500
        })

        // If refresh token is also invalid (401), clear tokens and redirect
        if (response.status === 401) {
          console.warn('[HTTP] Refresh token is invalid, clearing tokens')
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          processQueue(error)
          isRefreshing = false
          
          // Redirect to login if not already there
          if (window.location.pathname !== '/auth') {
            window.location.href = '/auth?redirect=' + encodeURIComponent(window.location.pathname)
          }
          return Promise.reject(error)
        }

        if (response.status !== 200) {
          throw new Error(`Token refresh failed with status ${response.status}`)
        }

        const { access_token, refresh_token: newRefreshToken } = response.data

        // Update tokens in localStorage
        localStorage.setItem('access_token', access_token)
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken)
        }

        // Update authorization header
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`
        }

        // Process queued requests
        processQueue()

        // Retry original request
        return http(originalRequest)
      } catch (refreshError: any) {
        // Refresh failed - clear tokens if it's a 401 (invalid refresh token)
        if (refreshError.response?.status === 401) {
          console.warn('[HTTP] Refresh token is invalid, clearing tokens')
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          processQueue(refreshError)
          isRefreshing = false
          
          // Redirect to login if not already there
          if (window.location.pathname !== '/auth') {
            window.location.href = '/auth?redirect=' + encodeURIComponent(window.location.pathname)
          }
        } else {
          // Other errors - just reject, let the app handle
          processQueue(refreshError)
          console.warn('[HTTP] Token refresh failed:', refreshError.message)
        }
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // Map 402/403 with limit/quota indication to structured error for upgrade modal
    const status = error.response?.status
    if (status === 402 || status === 403) {
      const data = error.response?.data as { resource?: string; limit_type?: SubscriptionLimitType } | undefined
      const limitType: SubscriptionLimitType =
        (data?.limit_type && ['edits', 'posts', 'images', 'topics', 'generic'].includes(data.limit_type))
          ? data.limit_type
          : (data?.resource && ['edits', 'posts', 'images', 'topics'].includes(data.resource))
            ? (data.resource as SubscriptionLimitType)
            : 'generic'
      return Promise.reject(new SubscriptionLimitError(limitType, status))
    }

    return Promise.reject(error)
  }
)

export default http
